import type {
  KnowledgeKind, MemoryFact, MemoryFactsPage, MemoryFactsQuery, MemoryTopics, PeopleRoster, PersonId, TeamMember,
} from "@don/wire";

import { mergePage, SECTION_KINDS, sortFacts } from "./memory.js";

/**
 * The memory column's read state (issue 277; PRD module 2, design-spec §9.1, §9.7), pure and
 * outside any render function for the reason `settings/team.ts` is: the rules worth asserting
 * are what a selection means, and `view.test.ts` asserts each one without a browser.
 *
 * **Memory reads wait for the roster.** An id is selected only once the Team roster has been
 * read and holds it; while the roster is loading or failed, and for an id it lacks, nobody is
 * selected and no topics read fires (ux-spec D13: another org's person looks like nobody).
 *
 * Every read is tagged with the person and a sequence number, and a settle whose tag is not
 * the current one is dropped, so a slow answer for the previous person never lands on this
 * one. Issue 278 adds Try again on a failed read; 279 the open topic and its pages; 281 the
 * Topic | Type toggle and the six kind sections, each paged on its own tag.
 */

/** Where the Team roster read is: the Team table's own three states (`TeamScreen`). */
export type RosterRead =
  | { readonly kind: "loading" }
  | { readonly kind: "failed"; readonly retrying: boolean }
  | { readonly kind: "loaded"; readonly roster: PeopleRoster };

/** The person `personId` names, only when a read roster holds them; otherwise `null`. */
export function selectable(roster: RosterRead, personId: PersonId | null): TeamMember | null {
  if (personId === null || roster.kind !== "loaded") return null;
  return roster.roster.people.find((m) => m.person_id === personId) ?? null;
}

export type TopicsState =
  | { readonly kind: "idle" }
  | { readonly kind: "loading" }
  | { readonly kind: "read"; readonly topics: MemoryTopics }
  /** `retrying` while Try again's read is in flight: the box stays, its button reads "Trying…". */
  | { readonly kind: "failed"; readonly retrying: boolean };

export interface ReadTag {
  readonly personId: PersonId;
  readonly seq: number;
}

/**
 * One topic's facts (issue 279): the first page outstanding or failed, or the facts loaded so
 * far, sorted (`sortFacts`, `mergePage`), with the token for the next page and where Show more
 * is.
 */
export type FactsState =
  | { readonly kind: "loading" }
  /** The first page failed; `retrying` while Try again's read is in flight. */
  | { readonly kind: "failed"; readonly retrying: boolean }
  | {
      readonly kind: "read";
      readonly facts: readonly MemoryFact[];
      /** `null`: nothing more to load, so no Show more. */
      readonly next: string | null;
      readonly more: "idle" | "loading" | "failed";
    };

/** One paged list of facts and the tag its outstanding page read carries: an open topic, a kind section. */
export interface Slot {
  /** The `pageSeq` of the read this slot waits on; a settle with another is dropped. */
  readonly seq: number;
  readonly facts: FactsState;
}

/** The one open topic (ux-spec D27). */
export interface OpenTopic extends Slot {
  /** The topic's name: the key its page reads and settles carry. */
  readonly key: string;
}

/** How the memory column groups facts (design-spec §9.7, D35): page state, kept across people. */
export type Group = "topic" | "type";

/** The six kind sections of the Type view (issue 281), by wire kind. */
export type KindSections = Readonly<Record<KnowledgeKind, Slot>>;

export interface MemoryView {
  /** The selected person, or `null` for nobody. */
  readonly person: PersonId | null;
  /** Moves on every selection, so a tag from an earlier one never matches. */
  readonly seq: number;
  readonly topics: TopicsState;
  /** The open topic, or `null` with every topic closed (issue 279). */
  readonly open: OpenTopic | null;
  /** Moves on every facts read, never back, so a page tag is used once. */
  readonly pageSeq: number;
  /** Topic or Type (issue 281); survives `personChanged`, gone on reload. */
  readonly group: Group;
  /**
   * The Type view's sections for the selected person, `null` until Type is first entered for
   * them; kept across toggles, dropped on switching person (design-spec §9.1).
   */
  readonly kinds: KindSections | null;
}

/** A read the screen must send to the memory source. */
export type MemoryRead =
  | { readonly method: "topics"; readonly tag: ReadTag }
  | {
      readonly method: "facts";
      /** The open topic's page; `key` is the topic's name. */
      readonly scope: "topic";
      readonly key: string;
      readonly tag: ReadTag;
      readonly query: MemoryFactsQuery;
    }
  | {
      readonly method: "facts";
      /** A kind section's page (issue 281); `key` is the wire kind. */
      readonly scope: "kind";
      readonly key: KnowledgeKind;
      readonly tag: ReadTag;
      readonly query: MemoryFactsQuery;
    };

export interface Step {
  readonly view: MemoryView;
  readonly reads: readonly MemoryRead[];
}

export const NOBODY: MemoryView = {
  person: null, seq: 0, topics: { kind: "idle" }, open: null, pageSeq: 0, group: "topic", kinds: null,
};

/**
 * The URL's person or the roster changed. The same selection again is no change (no read), so
 * a re-render or a re-run effect cannot ask twice.
 */
export function personChanged(view: MemoryView, roster: RosterRead, personId: PersonId | null): Step {
  const person = selectable(roster, personId)?.person_id ?? null;
  if (person === view.person) return { view, reads: [] };
  const seq = view.seq + 1;
  // The open topic, the kind sections and their pages belong to the person they were read for
  // (design-spec §9.1); the group is the page's (D35).
  const { group, pageSeq } = view;
  if (person === null) return { view: { ...NOBODY, seq, pageSeq, group }, reads: [] };
  const step: Step = {
    view: { person, seq, topics: { kind: "loading" }, open: null, pageSeq, group, kinds: null },
    reads: [{ method: "topics", tag: { personId: person, seq } }],
  };
  if (group === "topic") return step;
  const kinds = kindsRead(step.view);
  return { view: kinds.view, reads: [...step.reads, ...kinds.reads] };
}

/**
 * The Topic | Type toggle pressed (issue 281). The first entry to Type for the selected person
 * reads the six kinds' first pages in parallel, in `SECTION_KINDS` order, each under its own
 * tag; a return to Type for the same person reads nothing.
 */
export function groupChanged(view: MemoryView, group: Group): Step {
  const next = view.group === group ? view : { ...view, group };
  if (group === "topic" || next.person === null || next.kinds !== null) return { view: next, reads: [] };
  return kindsRead(next);
}

function kindsRead(view: MemoryView): Step {
  let pageSeq = view.pageSeq;
  const reads: MemoryRead[] = [];
  const kinds = {} as Record<KnowledgeKind, Slot>;
  for (const kind of SECTION_KINDS) {
    pageSeq += 1;
    kinds[kind] = { seq: pageSeq, facts: { kind: "loading" } };
    reads.push({
      method: "facts", scope: "kind", key: kind, tag: { personId: view.person!, seq: pageSeq },
      query: { topic: null, kind, page_token: null },
    });
  }
  return { view: { ...view, pageSeq, kinds }, reads };
}

const current = (view: MemoryView, tag: ReadTag) => view.person === tag.personId && view.seq === tag.seq;

/** A topics read answered; dropped unless it is the current selection's. */
export function topicsSettled(view: MemoryView, tag: ReadTag, topics: MemoryTopics): MemoryView {
  return current(view, tag) ? { ...view, topics: { kind: "read", topics } } : view;
}

/** A topics read failed; dropped unless it is the current selection's. */
export function topicsFailed(view: MemoryView, tag: ReadTag): MemoryView {
  return current(view, tag) ? { ...view, topics: { kind: "failed", retrying: false } } : view;
}

/**
 * Try again on a failed topics read (issue 278): the same person read again under a new tag,
 * the failure kept on screen as "Trying…" until it settles. Anything but a failed read not
 * already retrying asks nothing.
 */
export function topicsRetried(view: MemoryView): Step {
  if (view.person === null || view.topics.kind !== "failed" || view.topics.retrying) return { view, reads: [] };
  const seq = view.seq + 1;
  return {
    view: { ...view, seq, topics: { kind: "failed", retrying: true } },
    reads: [{ method: "topics", tag: { personId: view.person, seq } }],
  };
}

/** The next page tag, and the slot that waits on it. */
function slotRead(view: MemoryView, facts: FactsState): { pageSeq: number; tag: ReadTag; slot: Slot } {
  const pageSeq = view.pageSeq + 1;
  return { pageSeq, tag: { personId: view.person!, seq: pageSeq }, slot: { seq: pageSeq, facts } };
}

/** A page answered for a slot whose tag it carries: a first page is the facts, a later one merges in. */
function slotSettled(slot: Slot, page: MemoryFactsPage): FactsState {
  const facts = slot.facts.kind === "read" ? mergePage(slot.facts.facts, page) : sortFacts(page.facts);
  return { kind: "read", facts, next: page.next_page_token, more: "idle" };
}

/** A page failed: a first page is the slot's ProblemBox; a later one keeps the facts. */
function slotFailed(slot: Slot): FactsState {
  return slot.facts.kind === "read" ? { ...slot.facts, more: "failed" } : { kind: "failed", retrying: false };
}

/**
 * What Show more (or Try again under a failed Show more) asks for: the facts marked loading
 * and the token they came with. `null` while one is in flight or with no next page.
 */
function slotMore(slot: Slot): { facts: FactsState; token: string } | null {
  if (slot.facts.kind !== "read" || slot.facts.next === null || slot.facts.more === "loading") return null;
  return { facts: { ...slot.facts, more: "loading" }, token: slot.facts.next };
}

/** Try again: under a failed Show more it is `slotMore`; on a failed first page, the first page again. */
function slotRetry(slot: Slot): { facts: FactsState; token: string | null } | null {
  if (slot.facts.kind === "read") return slotMore(slot);
  if (slot.facts.kind !== "failed" || slot.facts.retrying) return null;
  return { facts: { kind: "failed", retrying: true }, token: null };
}

const tagged = (view: MemoryView, slot: Slot | null | undefined, tag: ReadTag): slot is Slot =>
  slot != null && view.person === tag.personId && slot.seq === tag.seq;

/** A facts read for topic `key` from `page_token`, under a fresh page tag. */
function pageRead(view: MemoryView, key: string, facts: FactsState, page_token: string | null): Step {
  const { pageSeq, tag, slot } = slotRead(view, facts);
  return {
    view: { ...view, pageSeq, open: { key, ...slot } },
    reads: [{ method: "facts", scope: "topic", key, tag, query: { topic: key, kind: null, page_token } }],
  };
}

/**
 * A topic row pressed open (issue 279, D27): any other open topic closes, and the topic's
 * first page is read afresh, on every open. Only once the person's topics are read.
 */
export function topicOpened(view: MemoryView, key: string): Step {
  if (view.person === null || view.topics.kind !== "read") return { view, reads: [] };
  return pageRead(view, key, { kind: "loading" }, null);
}

/** The open topic's row pressed: every topic closed, and a late page for it dropped. */
export function topicClosed(view: MemoryView): MemoryView {
  return view.open === null ? view : { ...view, open: null };
}

const openFor = (view: MemoryView, key: string, tag: ReadTag) =>
  view.open !== null && view.open.key === key && tagged(view, view.open, tag) ? view.open : null;

/**
 * A page answered. Dropped unless it is the read the open topic waits on (a closed or
 * reopened topic, another person). A first page is the facts; a later one merges in.
 */
export function pageSettled(view: MemoryView, key: string, tag: ReadTag, page: MemoryFactsPage): MemoryView {
  const open = openFor(view, key, tag);
  return open === null ? view : { ...view, open: { ...open, facts: slotSettled(open, page) } };
}

/** A page failed, dropped as `pageSettled` drops. A first page is the topic's ProblemBox; a later one keeps the facts. */
export function pageFailed(view: MemoryView, key: string, tag: ReadTag): MemoryView {
  const open = openFor(view, key, tag);
  return open === null ? view : { ...view, open: { ...open, facts: slotFailed(open) } };
}

/**
 * Show more, or Try again under a failed Show more: the next page with the token the shown
 * facts came with, the same token again after a failure. Nothing while one is in flight or
 * when there is no next page.
 */
export function more(view: MemoryView, key: string): Step {
  const open = view.open;
  const next = open !== null && open.key === key ? slotMore(open) : null;
  return next === null ? { view, reads: [] } : pageRead(view, key, next.facts, next.token);
}

/**
 * Try again on a failed first page: the first page read again under a new tag, the box kept as
 * "Trying…" until it settles. Under a failed Show more it is `more`.
 */
export function retry(view: MemoryView, key: string): Step {
  const open = view.open;
  const next = open !== null && open.key === key ? slotRetry(open) : null;
  return next === null ? { view, reads: [] } : pageRead(view, key, next.facts, next.token);
}

/** A facts read for kind section `kind` from `page_token`, under a fresh page tag of its own. */
function kindRead(view: MemoryView, kind: KnowledgeKind, facts: FactsState, page_token: string | null): Step {
  const { pageSeq, tag, slot } = slotRead(view, facts);
  return {
    view: { ...view, pageSeq, kinds: { ...view.kinds!, [kind]: slot } },
    reads: [{ method: "facts", scope: "kind", key: kind, tag, query: { topic: null, kind, page_token } }],
  };
}

const sectionFor = (view: MemoryView, kind: KnowledgeKind, tag: ReadTag) =>
  view.kinds !== null && tagged(view, view.kinds[kind], tag) ? view.kinds[kind] : null;

/** A kind section's page answered; dropped unless the section waits on this very tag (issue 281). */
export function kindSettled(view: MemoryView, kind: KnowledgeKind, tag: ReadTag, page: MemoryFactsPage): MemoryView {
  const slot = sectionFor(view, kind, tag);
  return slot === null ? view : { ...view, kinds: { ...view.kinds!, [kind]: { ...slot, facts: slotSettled(slot, page) } } };
}

/** A kind section's page failed, dropped as `kindSettled` drops. */
export function kindFailed(view: MemoryView, kind: KnowledgeKind, tag: ReadTag): MemoryView {
  const slot = sectionFor(view, kind, tag);
  return slot === null ? view : { ...view, kinds: { ...view.kinds!, [kind]: { ...slot, facts: slotFailed(slot) } } };
}

/** A kind section's Show more (or Try again under its failed Show more), as `more` is for a topic. */
export function kindMore(view: MemoryView, kind: KnowledgeKind): Step {
  const next = view.person !== null && view.kinds !== null ? slotMore(view.kinds[kind]) : null;
  return next === null ? { view, reads: [] } : kindRead(view, kind, next.facts, next.token);
}

/** A kind section's Try again, as `retry` is for a topic. */
export function kindRetry(view: MemoryView, kind: KnowledgeKind): Step {
  const next = view.person !== null && view.kinds !== null ? slotRetry(view.kinds[kind]) : null;
  return next === null ? { view, reads: [] } : kindRead(view, kind, next.facts, next.token);
}
